import { Component, Element, Event, EventEmitter, forceUpdate, h, Host, Listen, Method, Prop, State } from "@stencil/core";
import { debounce } from "../../utils/debounce";
import { renderStyles, RenderStylesProps } from "./slider-style-tags";
import Swiper from "swiper";
import { Autoplay, EffectCards, EffectFade, Grid, Navigation, Pagination } from "swiper/modules";
import { adjustSliderBreakpoints } from "./adjustsSliderBreakpoints";
import { i18nProvider } from "@trumpf/ux.i18n";
import { SwiperOptions } from "swiper/types/swiper-options";

Swiper.use([Pagination, Navigation, Autoplay, EffectFade, EffectCards, Grid]);

let sliderIdCounter = 0;

/**
 * @slot (default) - your slides (must each be wrapped in <ux-slider-slide>)
 * @slot pagination - for the <ux-slider-pagination> element
 */
@Component({
    tag: "ux-slider",
    styleUrl: "slider.scss",
    shadow: false
})
export class Slider {
    /**
     * The instance of the swiper slider
     */
    private swiper!: Swiper;
    /**
     * Top offset of the arrow navigation, used if arrowsFollowImg prop is set
     */
    @State() arrowsOffsetTop!: number;
    /**
     * Watches for slides outside of the viewport, and if they are present, activates the slider anyway
     */
    @State() hasOverflowingSlides = true;
    /**
     * True if slider on left position
     */
    @State() isBeginning = false;
    /**
     * True if slider on right position
     */
    @State() isEnd = false;

    @Element() slEl!: HTMLUxSliderElement;

    /** base class of the slider component */
    private baseClass = "ux-slider";

    /** slider components references */
    private swiperContainer!: HTMLDivElement;

    /** the unique id of the slider, can only contain letters and numbers */
    private sliderId = `${this.baseClass}-id-${sliderIdCounter++}`;
    /**
     * If set, the slider has autoplay on
     */
    @Prop() readonly autoplay: boolean = false;
    /**
     * Autoplay time in milliseconds
     */
    @Prop() readonly autoplayTime: number = 5000;
    /**
     * The slider will pause when the mouse hovers the slider
     * Can also be a CSS class selector, in which case the slider autoplay will stop when
     * the user hovers an element in the slider with the specified class.
     */
    @Prop() readonly pauseOnHover: boolean | string = false;
    /**
     * If set, the sliding will be infinite. Note: This can cause issues with certain web-components.
     * If you encounter such problems, disable loop.
     */
    @Prop() readonly loop: boolean = false;
    /**
     * If set, the slider will have no navigation arrows
     */
    @Prop() readonly noArrows: boolean = false;
    /**
     * Set the arrow navigation to be in the middle of the image, instead of the whole slider
     */
    @Prop() readonly arrowsFollowImg: boolean | string = false;
    /**
     * If set, the swiping will be disabled for mouse drags
     */
    @Prop() readonly noDrag: boolean = false;
    /**
     * Slide transition effect: one of: "slide", "fade", "cube", "coverflow", "flip"
     */
    @Prop() readonly effect: "slide" | "fade" | "cube" | "coverflow" | "flip" | "cards" = "slide";
    /**
     * The speed of the slide transitions
     */
    @Prop() readonly speed: number = 400;
    /** sets the number of visible slides on XL viewports */
    @Prop() readonly visibleSlidesXl: number | "auto" = 1;
    /** sets the number of visible slides on large viewports */
    @Prop() readonly visibleSlidesL: number | "auto" = 1;
    /** sets the number of visible slides on medium viewports */
    @Prop() readonly visibleSlidesM: number | "auto" = 1;
    /** sets the number of visible slides on small viewports */
    @Prop() readonly visibleSlidesS: number | "auto" = 1;
    /** sets the custom breakpoint for large viewports */
    @Prop() readonly breakpointL: number = 860;
    /** sets the custom breakpoint for medium viewports */
    @Prop() readonly breakpointM: number = 768;
    /** sets the custom breakpoint for small viewports */
    @Prop() readonly breakpointS: number = 567;
    /** sets the space between slides for XL viewports */
    @Prop() readonly spaceBetweenXl: number = 0;
    /** sets the space between slides for large viewports */
    @Prop() readonly spaceBetweenL: number = 0;
    /** sets the space between slides for medium viewports */
    @Prop() readonly spaceBetweenM: number = 0;
    /** sets the space between slides for small viewports */
    @Prop() readonly spaceBetweenS: number = 0;
    /** sets the inner container width for XL viewports */
    @Prop() readonly containerWidthXl: string = "100%";
    /** sets the inner container width for large viewports */
    @Prop() readonly containerWidthL: string = "100%";
    /** sets the inner container width for medium viewports */
    @Prop() readonly containerWidthM: string = "100%";
    /** sets the inner container width for small viewports */
    @Prop() readonly containerWidthS: string = "100%";
    /** sets the slides per group of for xl viewports */
    @Prop() readonly slidesPerGroupXl: number = 1;
    /** sets the slides per group of for large viewports */
    @Prop() readonly slidesPerGroupL: number = 1;
    /** sets the slides per group for medium viewports */
    @Prop() readonly slidesPerGroupM: number = 1;
    /** sets the slides per group for small viewports */
    @Prop() readonly slidesPerGroupS: number = 1;
    /**
     * Additional classes that will be added to an inner div,
     * used to set utility classes that will control the internal width of the slides
     */
    @Prop() readonly containerClasses: string = "";

    /**
     * If set, slider will be centered, with the addition of shadows
     * on the sides to cut off excess slides on tablet viewports
     *
     */
    @Prop() readonly fadedSides: boolean = false;
    /**
     * If set, the slides will have overflow visible, useful when the slides have outer shadow
     */
    @Prop() readonly slidesOverflowVisible: boolean = false;
    /**
     * If set, height of the slides will be aligned with the max height of the container
     */
    @Prop() readonly slidesAutoHeight: boolean = false;
    /**
     * If set, the height of the slider will be equal to the current visible slide
     */
    @Prop() readonly autoHeight: boolean = false;
    /**
     * If set, the slider will reinitialize itself on resize
     */
    @Prop() readonly reinitOnResize: boolean = false;
    /**
     * If set, the slider will start at a random index
     */
    @Prop() readonly startAtRandom: boolean = false;
    /**
     * Sets the direction of the slider
     */
    @Prop() readonly direction: "vertical" | "horizontal" = "horizontal";
    /**
     * Specifies the filling of slides on multiple row variants
     */
    @Prop() readonly slidesPerColumnFill: "row" | "column" = "row";
    /** sets the slides per columns for XL viewports */
    @Prop() readonly slidesPerColumnXl: number = 1;
    /** sets the slides per columns for large viewports */
    @Prop() readonly slidesPerColumnL: number = 1;
    /** sets the slides per columns for medium viewports */
    @Prop() readonly slidesPerColumnM: number = 1;
    /** sets the slides per columns for small viewports */
    @Prop() readonly slidesPerColumnS: number = 1;

    private pauseOnHoverInitialized = false;
    /**
     * Known bug on safari: the sidebar is not taken into consideration by swiper
     * in the calculation of breakpoints, therefore we need to "help" it with a manual
     * adjustment. The value holds the width of a possible sidebar in Safari.
     */
    private safariSidebarAdjustment: number = adjustSliderBreakpoints();

    private imgLazyCl = "ux-slider-lazy-img";
    private visibleCl = "ux-slider-slide-visible";
    private fullyVisibleCl = "ux-slider-slide-fully-visible";

    /**
     * Event to go to previous slide
     */
    @Event({
        eventName: "uxSliderUpdatePagination"
    })
    updatePagination!: EventEmitter;

    /**
     * Event emitted when the slider is initialized
     */
    @Event({
        eventName: "uxSliderInitialised"
    })
    sliderInitialised!: EventEmitter;

    @State() options: SwiperOptions = {
        slideClass: "ux-slider-slide",
        slidePrevClass: "ux-slider-slide-prev",
        slideActiveClass: "ux-slider-slide-active",
        slideVisibleClass: this.visibleCl,
        slideFullyVisibleClass: this.fullyVisibleCl,
        slideNextClass: "ux-slider-slide-next",
        wrapperClass: this.baseClass + "-wrapper",
        containerModifierClass: this.baseClass + "-container-",
        simulateTouch: !this.noDrag,
        effect: this.effect,
        fadeEffect: { crossFade: true },
        cardsEffect: {
            rotate: false,
            slideShadows: true,
            perSlideOffset: 15,
            perSlideRotate: 0
        },
        observer: true,
        autoplay: this.autoplay ? { delay: this.autoplayTime } : false,
        loop: this.loop,
        autoHeight: this.autoHeight,
        speed: this.speed,
        direction: this.direction,
        pagination: {
            el: null,
            clickable: true
        },
        grid: {
            rows: this.slidesPerColumnS,
            fill: this.slidesPerColumnFill
        },
        slidesPerView: this.visibleSlidesS,
        spaceBetween: this.spaceBetweenS,
        slidesPerGroup: this.slidesPerGroupS,
        watchSlidesProgress: true,
        breakpoints: {
            [this.breakpointS - this.safariSidebarAdjustment + 1]: {
                slidesPerView: this.visibleSlidesM,
                spaceBetween: this.spaceBetweenM,
                slidesPerGroup: this.slidesPerGroupM,
                grid: {
                    rows: this.slidesPerColumnM
                }
            },
            [this.breakpointM - this.safariSidebarAdjustment + 1]: {
                slidesPerView: this.visibleSlidesL,
                spaceBetween: this.spaceBetweenL,
                slidesPerGroup: this.slidesPerGroupL,
                grid: {
                    rows: this.slidesPerColumnL
                }
            },
            [this.breakpointL - this.safariSidebarAdjustment + 1]: {
                slidesPerView: this.visibleSlidesXl,
                spaceBetween: this.spaceBetweenXl,
                slidesPerGroup: this.slidesPerGroupXl,
                grid: {
                    rows: this.slidesPerColumnXl
                }
            }
        },
        on: {
            init: () => {
                this.handleArrowsPosition();
                this.sliderInitialised.emit({
                    id: this.sliderId
                });
            },
            paginationRender: () => {
                this.updatePagination.emit({
                    id: this.sliderId,
                    event: "paginationRender"
                });
            },
            slideNextTransitionStart: () => {
                this.updatePagination.emit({
                    id: this.sliderId,
                    event: "slideNextChangeStart"
                });
            },
            slidePrevTransitionStart: () => {
                this.updatePagination.emit({
                    id: this.sliderId,
                    event: "slidePrevChangeStart"
                });
            },
            slideChange: () => {
                this.checkSliderStartEnd();
            },
            autoplayTimeLeft: (s: Swiper, time: number, progress: number) => {
                const activeBullet = this.slEl.querySelector(".swiper-pagination-bullet-active") as HTMLElement;
                const progressBars = this.slEl.querySelectorAll(
                    ".swiper-pagination-bullet:not(.swiper-pagination-bullet-active) .swiper-pagination-progressbar"
                ) as NodeListOf<HTMLElement>;
                if (progressBars.length) {
                    progressBars.forEach((bullet) => {
                        bullet.style.width = "0%";
                    });
                }
                if (activeBullet) {
                    const progressBar = activeBullet.querySelector(".swiper-pagination-progressbar") as HTMLElement;
                    if (progressBar) {
                        progressBar.style.width = `${(1 - progress) * 100}%`;
                    }
                }
            }
        }
    };

    /**
     * Updates the number of slides per view of the slider
     * It expects a number as argument
     */
    @Method()
    async updateSlidesPerView(slidesPerView: number) {
        if (this.swiper === undefined) {
            return;
        }
        this.swiper.params.slidesPerView = slidesPerView;
        this.swiper.update();
    }

    /**
     * Updates the slider with the native update() method from swiper
     * It works effectively only for minor updates (size, spacing etc.)
     */
    @Method()
    async updateSoft() {
        if (this.swiper === undefined) {
            return;
        }
        this.swiper.update();
    }

    /**
     * Update method to be used to reset and update the slider completely.
     * It can receive an optional argument with valid swiper options.
     * See Swiper API for more information on valid options.
     */
    @Method()
    async update(additionalOptions = {}) {
        if (this.swiper === undefined) {
            return;
        }
        this.swiper.destroy();
        this.swiper = new Swiper(this.swiperContainer, {
            ...this.options,
            ...additionalOptions
        });
    }

    /**
     * Adds the slide passed as an argument to the slider.
     */
    @Method()
    async addSlide(slide: string | HTMLElement, before: boolean = false) {
        if (this.swiper === undefined) {
            return;
        }
        if (before) {
            this.swiper.prependSlide(slide);
        } else {
            this.swiper.appendSlide(slide);
        }
    }

    /**
     * Removes the slide that has the index specified as the argument from the slider
     */
    @Method()
    async removeSlide(index: number) {
        if (typeof index !== "number") {
            return;
        }
        this.swiperContainer?.querySelectorAll(".ux-slider-wrapper > ux-slider-slide")[index]?.remove();
        this.update();
    }

    /**
     * Go to next slide
     */
    @Method()
    async slideNext() {
        if (this.swiper === undefined) {
            return;
        }
        this.swiper.slideNext();
    }

    /**
     * Go to previous slide
     */
    @Method()
    async slidePrev() {
        if (this.swiper === undefined) {
            return;
        }
        this.swiper.slidePrev();
    }

    /**
     * Slide to a specific slide
     */
    @Method()
    async slideTo(index: number, velocity: number = 0) {
        if (this.swiper === undefined) {
            return;
        }
        this.swiper.slideTo(index, velocity);
    }

    @Listen("uxSliderStopAutoplay")
    sliderStopAutoplay(event: CustomEvent) {
        if (this.swiper === undefined) {
            return;
        }
        if (event.detail === this.sliderId) {
            this.swiper.autoplay.stop();
        }
    }

    @Listen("uxSliderResumeAutoplay")
    sliderResumeAutoplay(event: CustomEvent) {
        if (this.swiper === undefined) {
            return;
        }
        if (event.detail === this.sliderId || event.detail !== undefined) {
            this.swiper.autoplay.start();
        }
    }

    @Listen("uxSliderNextSlide")
    nextSlideHandler(event: CustomEvent): void {
        if (this.swiper === undefined) {
            return;
        }
        if (event.detail === this.sliderId) {
            this.swiper.slideNext();
        }
    }

    @Listen("uxSliderPreviousSlide")
    previousSlideHandler(event: CustomEvent): void {
        if (this.swiper === undefined) {
            return;
        }
        if (event.detail === this.sliderId) {
            this.swiper.slidePrev();
        }
    }

    private onImagesReady = debounce(() => {
        this.updatePagination.emit({
            id: this.sliderId,
            event: "imagesReady"
        });
        this.handleArrowsPosition();
    }, 100);

    /**
     * this function tries to mimic the behavior of the old image lazy loading of swiper which was removed in version 9
     */
    private initLazyImages = () => {
        const hasLazyImages = this.slEl.querySelector(`img.${this.imgLazyCl}`) !== null;
        if (hasLazyImages) {
            this.slEl.querySelectorAll(`img.${this.imgLazyCl}`).forEach((img) => {
                img.addEventListener("load", this.onImagesReady);
                img.setAttribute("loading", "lazy");
                if (img.getAttribute("data-src") !== null) {
                    img.setAttribute("src", img.getAttribute("data-src") as string);
                }
            });
            this.slEl.querySelectorAll(`source.${this.imgLazyCl}`).forEach((source) => {
                if (source.getAttribute("data-srcset") !== null) {
                    source.setAttribute("srcset", source.getAttribute("data-srcset") as string);
                }
            });
        } else {
            this.onImagesReady();
        }
    };

    componentWillLoad() {
        i18nProvider.subscribe(() => {
            forceUpdate(this);
        });
        const paginationElement = this.slEl.querySelector("ux-slider-pagination");
        if (paginationElement !== null) {
            paginationElement.sliderId = this.sliderId;
        }
        this.initLazyImages();
        this.initPauseOnHover();
    }

    componentDidLoad() {
        const paginationElement = this.slEl.querySelector("ux-slider-pagination");
        let paginationLabel = false;
        let paginationProgress = false;
        if (paginationElement !== null) {
            paginationLabel = !!paginationElement.label;
            paginationProgress = !!paginationElement.progress;
        }
        this.options.pagination = {
            ...(this.options.pagination as object),
            el:
                (paginationElement?.querySelector(".ux-slider-pagination__container") as HTMLElement) ||
                `.ux-slider-pagination[slider-id="${this.sliderId}"] .ux-slider-pagination__container`,
            renderBullet: function (index: number, className: string) {
                const slide = (this as { slides: HTMLElement[] }).slides[index] as HTMLUxSliderSlideElement;
                const label = slide.label || index + 1;
                let bulletClass = className;
                let bulletContent = "";
                if (paginationLabel) {
                    bulletClass += " swiper-pagination-bullet--labeled";
                    bulletContent += `<div class="ux-slider-pagination-label ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase">${label}</div>`;
                }
                if (paginationProgress) {
                    bulletContent += `<div class="swiper-pagination-progressbar"></div>`;
                }
                return `<span class="${bulletClass}">${bulletContent}</span>`;
            }
        };
        this.options.navigation = !this.noArrows
            ? {
                  nextEl:
                      (this.slEl.querySelector(`.${this.baseClass}-button-next`) as HTMLElement) ||
                      `.ux-slider[slider-id="${this.sliderId}"] .${this.baseClass}-button-next`,
                  prevEl:
                      (this.slEl.querySelector(`.${this.baseClass}-button-prev`) as HTMLElement) ||
                      `.ux-slider[slider-id="${this.sliderId}"] .${this.baseClass}-button-prev`
              }
            : {
                  nextEl: null,
                  prevEl: null
              };
        this.swiper = new Swiper(this.swiperContainer, this.options);
        this.calculateSlidesOverflow();
        if (this.startAtRandom) {
            const randomPosition = Math.floor(Math.random() * (this.swiper.slides.length - 1) + 0);
            this.swiper.slideTo(randomPosition, 0);
        }
        if (!this.withIntObs) {
            return;
        }
        this.checkSliderStartEnd();
    }

    private handleArrowsPosition = () => {
        if (this.arrowsFollowImg === false) {
            return;
        }
        let activeSlide: HTMLElement | null = this.slEl.querySelector(`.ux-slider-slide-active`);
        if (activeSlide === null) {
            activeSlide = this.slEl.querySelector(`.ux-slider-slide`);
        }
        if (activeSlide === null) {
            return;
        }
        const selector = typeof this.arrowsFollowImg === "string" && this.arrowsFollowImg.length > 0 ? this.arrowsFollowImg : "img";
        const slideImage = activeSlide.querySelector(selector) as HTMLElement;
        if (slideImage === null) {
            return;
        }
        const activeSlideStyles = window.getComputedStyle(activeSlide);
        let activeSlideMarginTop = parseFloat(activeSlideStyles.getPropertyValue("margin-top"));
        if (isNaN(activeSlideMarginTop)) {
            activeSlideMarginTop = 0;
        }
        const slideImageOffsetTop =
            slideImage.offsetTop >= 0
                ? slideImage.offsetTop
                : (slideImage.parentNode as HTMLElement).offsetTop + slideImage.offsetTop;
        const slideImageOffsetHeight = slideImage.offsetHeight / 2;
        const arrowsOffsetTop = slideImageOffsetTop + slideImageOffsetHeight + activeSlideMarginTop;
        if (this.arrowsOffsetTop === arrowsOffsetTop) {
            return;
        }
        this.arrowsOffsetTop = arrowsOffsetTop;
    };

    private checkSliderStartEnd = () => {
        if (this.swiper && !this.noArrows && !this.loop) {
            this.isBeginning = !!this.swiper.isBeginning;
            this.isEnd = !!this.swiper.isEnd;
            this.updatePagination.emit({
                id: this.sliderId,
                event: "checkSliderStartEnd",
                isBeginning: this.isBeginning,
                isEnd: this.isEnd
            });
        }
    };

    private onResize = debounce(async () => {
        if (this.reinitOnResize) {
            await this.update();
            this.handleArrowsPosition();
            this.calculateSlidesOverflow();
            return;
        }
        if (typeof this.swiper !== "undefined") {
            this.swiper.update();
        }
        this.handleArrowsPosition();
        this.calculateSlidesOverflow();
    }, 200);

    private handleMouseActions = (mouseAction: string) => (e: Event) => {
        if (this.swiper === undefined) {
            return;
        }
        if (typeof this.pauseOnHover === "string" && this.pauseOnHover.length > 0) {
            const pauseElement = document.querySelector(`.${this.pauseOnHover}`);
            if (pauseElement === null) {
                return;
            }
            if ((e.target as HTMLElement).closest(`.${this.pauseOnHover}`) !== null) {
                if (mouseAction === "over") {
                    this.swiper.autoplay.pause();
                }
            } else if (this.swiper.autoplay.paused) {
                this.swiper.autoplay.resume();
            }
        } else {
            if (mouseAction === "over") {
                this.swiper.autoplay.pause();
            } else if (mouseAction === "leave" && this.swiper.autoplay.paused) {
                this.swiper.autoplay.resume();
            }
        }
    };

    private handleMouseOver = this.handleMouseActions("over");
    private handleMouseLeave = this.handleMouseActions("leave");

    private initPauseOnHover = () => {
        if (this.pauseOnHover !== false && this.slEl !== undefined && !this.pauseOnHoverInitialized) {
            this.slEl.addEventListener("mouseover", this.handleMouseOver);
            this.slEl.addEventListener("mouseleave", this.handleMouseLeave);
            this.pauseOnHoverInitialized = true;
        }
    };

    connectedCallback() {
        this.initPauseOnHover();
        window.addEventListener("resize", this.onResize);
    }

    disconnectedCallback() {
        if (this.pauseOnHover !== false && this.pauseOnHoverInitialized) {
            this.slEl.removeEventListener("mouseover", this.handleMouseOver);
            this.slEl.removeEventListener("mouseleave", this.handleMouseLeave);
            this.pauseOnHoverInitialized = false;
        }
        window.removeEventListener("resize", this.onResize);
    }

    private calculateSlidesOverflow = async () => {
        const slides = this.swiperContainer.querySelectorAll(".ux-slider-slide:not(.ux-slider-slide-duplicate)");
        const visibleSlides = this.swiperContainer.querySelectorAll(`.${this.fullyVisibleCl}`);
        this.hasOverflowingSlides = slides.length > visibleSlides.length;
        if (typeof this.swiper !== "undefined") {
            if (!this.hasOverflowingSlides && (this.loop || this.autoplay || this.pauseOnHover)) {
                await this.update({
                    loop: false,
                    autoplay: false,
                    pauseOnHover: false
                });
            }
            this.swiper.allowTouchMove = this.hasOverflowingSlides;
        }
        if (this.handleDuplicatesFlag) {
            return;
        }
        this.handleDuplicatesFlag = this.hasOverflowingSlides;
    };

    /** manages the visible state of duplicates slider when there is no overflow */
    @State() handleDuplicatesFlag = false;

    private withIntObs = "IntersectionObserver" in window;
    /** END: loading performance optimization */

    render() {
        const {
            baseClass,
            noArrows,
            sliderId,
            fadedSides,
            containerClasses,
            slidesOverflowVisible,
            slidesAutoHeight,
            hasOverflowingSlides
        } = this;

        const sliderContainerClasses = [`${baseClass}__inner-container`];
        if (containerClasses.length > 0) {
            sliderContainerClasses.push(containerClasses);
        }

        const { t } = i18nProvider;

        return (
            <Host
                slider-id={sliderId}
                class={{
                    [baseClass]: true,
                    [`${baseClass}--faded-sides`]: fadedSides,
                    [`${baseClass}--slides-overflow-visible`]: slidesOverflowVisible || this.effect === "cards",
                    [`${baseClass}--slides-auto-height`]: slidesAutoHeight,
                    [`${baseClass}--cards`]: this.effect === "cards"
                }}
            >
                <div class={`${baseClass}__content-container`}>
                    {fadedSides && (
                        <div
                            class={`${baseClass}__faded-side ${baseClass}__faded-side--left`}
                            style={{
                                visibility: hasOverflowingSlides ? "inherit" : "hidden"
                            }}
                            onClick={() => this.swiper?.slidePrev()}
                        />
                    )}
                    {fadedSides && (
                        <div
                            class={`${baseClass}__faded-side ${baseClass}__faded-side--right`}
                            style={{
                                visibility: hasOverflowingSlides ? "inherit" : "hidden"
                            }}
                            onClick={() => this.swiper?.slideNext()}
                        />
                    )}
                    {renderStyles(this as unknown as RenderStylesProps)}
                    <div class={sliderContainerClasses.join(" ")}>
                        <div
                            class={{
                                [`${baseClass}-container`]: true,
                                [`${baseClass}-container-fade`]: this.effect === "fade"
                            }}
                            ref={(el) => (this.swiperContainer = el as HTMLDivElement)}
                        >
                            <div class={`${baseClass}-wrapper`}>
                                <slot />
                            </div>
                        </div>
                    </div>
                    {!noArrows && this.hasOverflowingSlides && (
                        <button
                            class={`${baseClass}-button-prev ux-button ux-button--icon-only ux-slider-button`}
                            type="button"
                            aria-label={t("slider.prevButton.label")}
                            {...(this.isBeginning && { "aria-disabled": "true" })}
                        >
                            <ux-icon name="chevron-left" weight="light" />
                        </button>
                    )}
                    {!noArrows && this.hasOverflowingSlides && (
                        <button
                            class={`${baseClass}-button-next ux-button ux-button--icon-only ux-slider-button`}
                            type="button"
                            aria-label={t("slider.nextButton.label")}
                            {...(this.isEnd && { "aria-disabled": "true" })}
                        >
                            <ux-icon name="chevron-right" weight="light" />
                        </button>
                    )}
                </div>
                <slot name="pagination" />
            </Host>
        );
    }
}
